import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { ChevronUp, ChevronDown, Save, RotateCcw, Columns3, Type, Rows3, Grid3x3 } from "lucide-react";
import { FIELD_NAMES, BUTTON_NAMES, BG_NAMES, DENSITY, GRID_COLORS } from "../lib/invoiceUi";

const Section = ({ icon: Icon, title, children }) => (
  <div className="space-y-3 border border-slate-200 rounded-sm p-3 bg-white">
    <h3 className="font-[Chivo] font-semibold text-sm flex items-center gap-2 text-slate-900"><Icon className="w-4 h-4" /> {title}</h3>
    {children}
  </div>
);

const Row = ({ label, children }) => (
  <div className="flex items-center justify-between gap-3 text-sm">
    <span className="text-slate-600">{label}</span>
    <div className="flex items-center gap-2">{children}</div>
  </div>
);

const move = (arr, i, dir) => {
  const j = i + dir;
  if (j < 0 || j >= arr.length) return arr;
  const copy = [...arr];
  [copy[i], copy[j]] = [copy[j], copy[i]];
  return copy;
};

export const InvoiceUiCustomizer = ({ open, onOpenChange, ui, update, onSave, onReset, onCancel }) => {
  const [saving, setSaving] = useState(false);
  const setHeader = (p) => update((u) => ({ ...u, header: { ...u.header, ...p } }));
  const setFormat = (p) => update((u) => ({ ...u, format: { ...u.format, ...p } }));
  const setLabel = (k, v) => update((u) => ({ ...u, labels: { ...u.labels, [k]: v } }));
  const setCol = (key, p) => update((u) => ({ ...u, columns: u.columns.map((c) => (c.key === key ? { ...c, ...p } : c)) }));
  const moveCol = (i, dir) => update((u) => ({ ...u, columns: move(u.columns, i, dir) }));

  const save = async () => { setSaving(true); const ok = await onSave(); setSaving(false); if (ok) onOpenChange(false); };

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onCancel(); onOpenChange(v); }}>
      <SheetContent side="right" className="w-[440px] sm:max-w-[440px] overflow-y-auto bg-slate-50" data-testid="invoice-ui-customizer">
        <SheetHeader>
          <SheetTitle className="font-[Chivo]">Personnaliser l'écran Facture</SheetTitle>
          <SheetDescription>Les modifications s'affichent en direct. Enregistrez pour les appliquer à tous les utilisateurs.</SheetDescription>
        </SheetHeader>

        <div className="space-y-4 mt-4 pb-24">
          <Section icon={Rows3} title="En-tête de la facture">
            <p className="text-xs text-slate-500">Ordre et affichage des champs</p>
            {ui.header.fields.map((f, i) => (
              <div key={f} className="flex items-center gap-2 text-sm" data-testid={`ui-field-${f}`}>
                <Switch checked={!ui.header.hiddenFields.includes(f)} onCheckedChange={(v) => setHeader({ hiddenFields: v ? ui.header.hiddenFields.filter((x) => x !== f) : [...ui.header.hiddenFields, f] })} />
                <Input value={ui.labels[f]} onChange={(e) => setLabel(f, e.target.value)} className="h-8 rounded-sm flex-1" title={`Libellé — ${FIELD_NAMES[f]}`} />
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setHeader({ fields: move(ui.header.fields, i, -1) })}><ChevronUp className="w-4 h-4" /></Button>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setHeader({ fields: move(ui.header.fields, i, 1) })}><ChevronDown className="w-4 h-4" /></Button>
              </div>
            ))}
            <Row label="Position des boutons">
              <Select value={ui.header.buttonsPosition} onValueChange={(v) => setHeader({ buttonsPosition: v })}>
                <SelectTrigger className="h-8 w-40 rounded-sm" data-testid="ui-buttons-position"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="right">À droite</SelectItem><SelectItem value="left">À gauche</SelectItem><SelectItem value="below">Sous l'en-tête</SelectItem></SelectContent>
              </Select>
            </Row>
            <p className="text-xs text-slate-500">Ordre et libellés des boutons</p>
            {ui.header.buttons.map((b, i) => (
              <div key={b} className="flex items-center gap-2 text-sm" data-testid={`ui-button-${b}`}>
                <span className="w-28 text-xs text-slate-500">{BUTTON_NAMES[b]}</span>
                <Input value={ui.labels[b]} onChange={(e) => setLabel(b, e.target.value)} className="h-8 rounded-sm flex-1" />
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setHeader({ buttons: move(ui.header.buttons, i, -1) })}><ChevronUp className="w-4 h-4" /></Button>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setHeader({ buttons: move(ui.header.buttons, i, 1) })}><ChevronDown className="w-4 h-4" /></Button>
              </div>
            ))}
          </Section>

          <Section icon={Columns3} title="Colonnes des lignes">
            <p className="text-xs text-slate-500">Afficher, renommer, réordonner, largeur (px, 0 = automatique)</p>
            {ui.columns.map((c, i) => (
              <div key={c.key} className="flex items-center gap-2 text-sm" data-testid={`ui-col-${c.key}`}>
                <Switch checked={c.visible} disabled={c.key === "article"} onCheckedChange={(v) => setCol(c.key, { visible: v })} />
                <Input value={c.label} onChange={(e) => setCol(c.key, { label: e.target.value })} className="h-8 rounded-sm flex-1" data-testid={`ui-col-label-${c.key}`} />
                <Input type="number" value={c.width} min={0} onChange={(e) => setCol(c.key, { width: Math.max(0, parseInt(e.target.value) || 0) })} className="h-8 w-20 rounded-sm font-mono" title="Largeur (px)" />
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => moveCol(i, -1)}><ChevronUp className="w-4 h-4" /></Button>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => moveCol(i, 1)}><ChevronDown className="w-4 h-4" /></Button>
              </div>
            ))}
          </Section>

          <Section icon={Type} title="Écritures et formats">
            <Row label="Taille des écritures">
              <Select value={ui.format.fontSize} onValueChange={(v) => setFormat({ fontSize: v })}>
                <SelectTrigger className="h-8 w-32 rounded-sm" data-testid="ui-font-size"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="xs">Petite</SelectItem><SelectItem value="sm">Normale</SelectItem><SelectItem value="base">Grande</SelectItem></SelectContent>
              </Select>
            </Row>
            <Row label="Espacement des lignes">
              <Select value={ui.format.density || "compact"} onValueChange={(v) => setFormat({ density: v })}>
                <SelectTrigger className="h-8 w-32 rounded-sm" data-testid="ui-density"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(DENSITY).map(([k, v]) => <SelectItem key={k} value={k}>{v.name}</SelectItem>)}</SelectContent>
              </Select>
            </Row>
            <Row label="Format de date">
              <Select value={ui.format.dateFormat} onValueChange={(v) => setFormat({ dateFormat: v })}>
                <SelectTrigger className="h-8 w-32 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="short">Court (JJ/MM/AAAA)</SelectItem><SelectItem value="long">Long</SelectItem></SelectContent>
              </Select>
            </Row>
            <Row label="Décimales des montants">
              <Select value={String(ui.format.decimals)} onValueChange={(v) => setFormat({ decimals: parseInt(v) })}>
                <SelectTrigger className="h-8 w-20 rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="0">0</SelectItem><SelectItem value="1">1</SelectItem><SelectItem value="2">2</SelectItem></SelectContent>
              </Select>
            </Row>
            <Row label="Afficher « F CFA »"><Switch checked={ui.format.currency} onCheckedChange={(v) => setFormat({ currency: v })} /></Row>
            <Row label="En-tête de la facture imprimée">
              <Select value={ui.format.headerStyle || "brand"} onValueChange={(v) => setFormat({ headerStyle: v })}>
                <SelectTrigger className="h-8 w-40 rounded-sm" data-testid="ui-header-style"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="brand">Bandeau vert (logo)</SelectItem><SelectItem value="classic">Classique (blanc)</SelectItem></SelectContent>
              </Select>
            </Row>
            <Row label="Fond de la facture">
              <Select value={ui.format.invoiceBg} onValueChange={(v) => setFormat({ invoiceBg: v })}>
                <SelectTrigger className="h-8 w-32 rounded-sm" data-testid="ui-invoice-bg"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(BG_NAMES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </Row>
            <div className="grid grid-cols-2 gap-2 pt-1">
              {[["title", "Titre de l'écran"], ["payment", "Mode de règlement"], ["discount", "Remise"], ["amountPaid", "Montant reçu"], ["total", "Total"], ["subtotal", "Sous-total"], ["balanceDue", "Reste à payer"], ["articles", "Fenêtre des articles"]].map(([k, l]) => (
                <div key={k}><Label className="text-[11px] text-slate-500">{l}</Label><Input value={ui.labels[k]} onChange={(e) => setLabel(k, e.target.value)} className="h-8 rounded-sm" data-testid={`ui-label-${k}`} /></div>
              ))}
              <div className="col-span-2"><Label className="text-[11px] text-slate-500">Texte de la ligne de saisie</Label><Input value={ui.labels.picker} onChange={(e) => setLabel("picker", e.target.value)} className="h-8 rounded-sm" /></div>
            </div>
          </Section>

          <Section icon={Grid3x3} title="Séparations de colonnes">
            <Row label="Lignes entre les colonnes"><Switch checked={ui.format.grid !== false} onCheckedChange={(v) => setFormat({ grid: v })} data-testid="ui-grid-enabled" /></Row>
            <Row label="Couleur des lignes">
              <Select value={GRID_COLORS[ui.format.gridColor] ? ui.format.gridColor : "custom"} onValueChange={(v) => { if (v !== "custom") setFormat({ gridColor: v }); }}>
                <SelectTrigger className="h-8 w-36 rounded-sm" data-testid="ui-grid-color"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(GRID_COLORS).map(([k, v]) => <SelectItem key={k} value={k}><span className="inline-flex items-center gap-2"><span className="w-3 h-3 rounded-sm border" style={{ background: k }} />{v}</span></SelectItem>)}
                  {!GRID_COLORS[ui.format.gridColor] && <SelectItem value="custom">Personnalisée</SelectItem>}
                </SelectContent>
              </Select>
              <input type="color" value={ui.format.gridColor || "#93c5fd"} onChange={(e) => setFormat({ gridColor: e.target.value })} className="h-8 w-9 p-0.5 border border-slate-300 rounded-sm bg-white cursor-pointer" title="Couleur personnalisée" data-testid="ui-grid-color-picker" />
            </Row>
            <Row label="Appliquer aussi à l'impression"><Switch checked={ui.format.gridPrint !== false} onCheckedChange={(v) => setFormat({ gridPrint: v })} data-testid="ui-grid-print" /></Row>
            <div className="rounded-sm p-2 text-xs bg-white border border-slate-200" style={{ "--inv-grid": ui.format.gridColor || "#93c5fd", opacity: ui.format.grid === false ? 0.4 : 1 }}>
              <table className="w-full inv-grid-table"><thead><tr>{["Article", "Unité", "Qté", "P.U.", "P.T."].map((h) => <th key={h} className="px-1 py-0.5 font-medium text-slate-600">{h}</th>)}</tr></thead>
                <tbody><tr>{["Ciment 50kg", "sac", "10", "4 500", "45 000"].map((v, i) => <td key={i} className="px-1 py-0.5 text-slate-700 font-mono text-[11px]">{v}</td>)}</tr></tbody></table>
            </div>
          </Section>
        </div>

        <div className="fixed bottom-0 right-0 w-[440px] max-w-full bg-white border-t border-slate-200 p-3 flex gap-2 justify-between">
          <Button variant="outline" className="rounded-sm text-red-700" onClick={onReset} data-testid="ui-reset-btn"><RotateCcw className="w-4 h-4 mr-2" /> Réinitialiser</Button>
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-sm" onClick={() => { onCancel(); onOpenChange(false); }}>Annuler</Button>
            <Button className="btn-accent" onClick={save} disabled={saving} data-testid="ui-save-btn"><Save className="w-4 h-4 mr-2" /> {saving ? "…" : "Enregistrer"}</Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
